import React, { useState } from 'react';
import { Drink } from '../App';
import './DrinkManager.css';

interface DrinkManagerProps {
  drinks: Drink[];
  onAddDrink: (name: string, basePrice: number, icon: string) => Promise<any>;
  onUpdateBasePrice: (drinkId: number, basePrice: number) => Promise<any>;
  onDeleteDrink: (drinkId: number) => Promise<any>;
}

const DrinkManager: React.FC<DrinkManagerProps> = ({ drinks, onAddDrink, onUpdateBasePrice, onDeleteDrink }) => {
  const [newDrinkName, setNewDrinkName] = useState('');
  const [newDrinkPrice, setNewDrinkPrice] = useState('');
  const [newDrinkIcon, setNewDrinkIcon] = useState('🍹');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [editingDrink, setEditingDrink] = useState<number | null>(null);
  const [editPrice, setEditPrice] = useState('');
  const [deletingDrink, setDeletingDrink] = useState<number | null>(null);

  const availableIcons = [
    '🍺', '🍻', '🥃', '🍷', '🍹', '🍸', '🥂', '🍾',
    '🍶', '🥤', '☕', '🧃', '🧉', '🍵', '🥛', '🍊',
    '🍋', '🍓', '🍑', '🥥', '🌶️', '🔥', '💀', '🎉'
  ];

  const handleAddDrink = async () => {
    if (!newDrinkName.trim() || !newDrinkPrice) {
      setMessage('❌ Bitte alle Felder ausfüllen!');
      return;
    }

    const price = parseFloat(newDrinkPrice);
    if (isNaN(price) || price <= 0) {
      setMessage('❌ Bitte einen gültigen Preis eingeben!');
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      await onAddDrink(newDrinkName, price, newDrinkIcon);
      setMessage('✅ Getränk erfolgreich hinzugefügt!');
      setNewDrinkName('');
      setNewDrinkPrice('');
      setNewDrinkIcon('🍹');
    } catch (error) {
      setMessage('❌ Fehler beim Hinzufügen!');
    } finally {
      setLoading(false);
    }
  };

  const handleEditBasePrice = async (drinkId: number) => {
    const price = parseFloat(editPrice);
    if (isNaN(price) || price <= 0) {
      setMessage('❌ Bitte einen gültigen Preis eingeben!');
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      await onUpdateBasePrice(drinkId, price);
      setMessage('✅ Basispreis erfolgreich geändert!');
      setEditingDrink(null);
      setEditPrice('');
    } catch (error) {
      setMessage('❌ Fehler beim Ändern des Preises!');
    } finally {
      setLoading(false);
    }
  };

  const startEditing = (drink: Drink) => {
    setEditingDrink(drink.id);
    setEditPrice(drink.basePrice.toString());
    setMessage('');
  };

  const cancelEditing = () => {
    setEditingDrink(null);
    setEditPrice('');
    setMessage('');
  };

  const handleDeleteDrink = async (drinkId: number) => {
    setLoading(true);
    setMessage('');

    try {
      await onDeleteDrink(drinkId);
      setMessage('✅ Getränk erfolgreich gelöscht!');
      setDeletingDrink(null);
    } catch (error) {
      setMessage('❌ Fehler beim Löschen!');
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = (drinkId: number) => {
    setDeletingDrink(drinkId);
    setMessage('');
  };

  const cancelDelete = () => {
    setDeletingDrink(null);
    setMessage('');
  };

  return (
    <div className="drink-manager">
      <h2>⚙️ Getränke-Verwaltung</h2>

      <div className="add-drink-section">
        <h3>Neues Getränk hinzufügen</h3>
        
        {message && (
          <div className={`message ${message.includes('✅') ? 'success' : 'error'}`}>
            {message}
          </div>
        )}

        <div className="form-group">
          <label>Name:</label>
          <input
            type="text"
            value={newDrinkName}
            onChange={(e) => setNewDrinkName(e.target.value)}
            placeholder="z.B. Jägermeister"
            maxLength={30}
          />
        </div>

        <div className="form-group">
          <label>Basispreis (€):</label>
          <input
            type="number"
            value={newDrinkPrice}
            onChange={(e) => setNewDrinkPrice(e.target.value)}
            placeholder="z.B. 2.50"
            step="0.50"
            min="0.50"
          />
        </div>

        <div className="form-group">
          <label>Icon:</label>
          <div className="icon-selector">
            {availableIcons.map(icon => (
              <button
                key={icon}
                className={`icon-button ${newDrinkIcon === icon ? 'selected' : ''}`}
                onClick={() => setNewDrinkIcon(icon)}
              >
                {icon}
              </button>
            ))}
          </div>
        </div>

        <button
          className="add-button"
          onClick={handleAddDrink}
          disabled={loading}
        >
          {loading ? 'Füge hinzu...' : 'Getränk hinzufügen'}
        </button>
      </div>

      <div className="existing-drinks">
        <h3>Vorhandene Getränke</h3>
        <div className="drinks-table">
          <div className="table-header">
            <span>Icon</span>
            <span>Name</span>
            <span>Basispreis</span>
            <span>Aktueller Preis</span>
            <span>Verkauft</span>
            <span>Aktionen</span>
          </div>
          {drinks.map(drink => (
            <div key={drink.id} className="table-row">
              <span>{drink.icon}</span>
              <span>{drink.name}</span>
              <span>
                {editingDrink === drink.id ? (
                  <div className="edit-price-container">
                    <input
                      type="number"
                      value={editPrice}
                      onChange={(e) => setEditPrice(e.target.value)}
                      step="0.50"
                      min="0.50"
                      className="edit-price-input"
                    />
                  </div>
                ) : (
                  `€${drink.basePrice.toFixed(2)}`
                )}
              </span>
              <span className={drink.currentPrice > drink.basePrice ? 'price-up' : drink.currentPrice < drink.basePrice ? 'price-down' : ''}>
                €{drink.currentPrice.toFixed(2)}
              </span>
              <span>{drink.purchases}</span>
              <span className="actions">
                {editingDrink === drink.id ? (
                  <div className="edit-actions">
                    <button
                      className="save-button"
                      onClick={() => handleEditBasePrice(drink.id)}
                      disabled={loading}
                    >
                      ✓
                    </button>
                    <button
                      className="cancel-button"
                      onClick={cancelEditing}
                      disabled={loading}
                    >
                      ✕
                    </button>
                  </div>
                ) : deletingDrink === drink.id ? (
                  <div className="delete-actions">
                    <span className="delete-confirmation">Wirklich löschen?</span>
                    <button
                      className="confirm-delete-button"
                      onClick={() => handleDeleteDrink(drink.id)}
                      disabled={loading}
                    >
                      ✓
                    </button>
                    <button
                      className="cancel-delete-button"
                      onClick={cancelDelete}
                      disabled={loading}
                    >
                      ✕
                    </button>
                  </div>
                ) : (
                  <div className="normal-actions">
                    <button
                      className="edit-button"
                      onClick={() => startEditing(drink)}
                      disabled={loading}
                    >
                      ✏️
                    </button>
                    <button
                      className="delete-button"
                      onClick={() => confirmDelete(drink.id)}
                      disabled={loading}
                    >
                      🗑️
                    </button>
                  </div>
                )}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DrinkManager;